import React, {useEffect, useState} from 'react';
import {useHistory, useParams} from 'react-router-dom';
import {Row, Col} from 'react-bootstrap';
import {StyledH4, WhiteButton, GreenButton} from '@styled';
import styled from 'styled-components';
import DownloadAndViewReport from '@modules/reports/DownloadAndViewReport';
import * as Yup from 'yup';
import {Tabs, Form, DropdownField} from '@components';
import {useDispatch} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {SHOW_REPORT_STATUSES} from '@app/utils/constants';
import moment from 'moment';
import NewComment from './NewComment';
import CommentsHistory from './CommentsHistory';
import {sagaActions} from './redux/saga';

const StyledRow = styled(Row)`
  background: #ffffff;
  box-shadow: 0px 1px 3px rgba(38, 38, 65, 0.1);
  border-radius: 5px;
`;
const StyledCol = styled(Col)`
  padding: 0.4em 0.6em;
`;

const STATUS_OPTIONS = [
  {
    label: 'Approved',
    value: 'Approved',
  },
  {
    label: 'Rejected',
    value: 'Rejected',
  },
];

const CorpReportReview = () => {
  const {reportId} = useParams();
  const history = useHistory();
  const [t] = useTranslation();
  const [reportData, setReportData] = useState(null);
  const [isReportVisible, toggleReportVisible] = useState(false);
  const dispatch = useDispatch();
  useEffect(() => {
    if (reportId) {
      const {state} = history.location;
      setReportData(state || null);
    } else {
      setReportData(null);
      history.push('/validators/reports');
    }
  }, []);

  const newCommentCB = React.useCallback(
    (isActive) => <NewComment isActive={isActive} report={reportData} />,
    [reportData]
  );
  const commentsHistoryCB = React.useCallback(
    (isActive) => <CommentsHistory isActive={isActive} report={reportData} />,
    [reportData]
  );

  const getTabItems = () => {
    const tabs = [
      {
        tabKey: 'new-comment',
        tabLabel: 'New Comment',
        position: 1,
        content: newCommentCB,
      },
      {
        tabKey: 'commentsHistory',
        tabLabel: 'Comments History',
        position: 2,
        content: commentsHistoryCB,
      },
    ];
    return tabs;
  };

  const handleSubmit = (values) => {
    dispatch({
      type: sagaActions.UPDATE_REPORT_STATUS,
      payload: {
        values: {...values, status: values.status.value},
        reportId,
        history,
      },
    });
  };

  const handleCancelReview = () => {
    setReportData(null);
    history.push('/validators/reports');
  };

  return (
    <div>
      <StyledH4>Review Report</StyledH4>
      {reportData ? (
        <>
          <Row>
            <StyledCol>Report ID</StyledCol>
            <StyledCol>Corporate Name</StyledCol>
            <StyledCol>Submitted On</StyledCol>
            <StyledCol>Status</StyledCol>
            <StyledCol>Executive Reporter</StyledCol>
            <StyledCol>Actions</StyledCol>
          </Row>
          <StyledRow>
            <StyledCol>{reportData.id}</StyledCol>
            <StyledCol>{reportData.corporateName}</StyledCol>
            <StyledCol>
              {moment(reportData.submittedOn).format('DD/MM/YYYY')}
            </StyledCol>
            <StyledCol>
              {SHOW_REPORT_STATUSES[reportData.status] || reportData.status}
            </StyledCol>
            <StyledCol>{reportData.executiveReporter}</StyledCol>
            <StyledCol>
              <WhiteButton
                onClick={() => toggleReportVisible(!isReportVisible)}
              >
                {isReportVisible ? 'Hide' : 'View/Download'}
              </WhiteButton>
            </StyledCol>
          </StyledRow>
          {isReportVisible ? (
            <DownloadAndViewReport reportId={reportData.id} />
          ) : null}
          <div className="mt-2">
            <Form
              initialValues={{
                comments: '',
                status: '',
              }}
              validationSchema={Yup.object({
                comments: Yup.string().when('status', {
                  is: (cc) => cc && cc.value === 'Rejected',
                  then: Yup.string().required(
                    t('owner.validators.reports.review.required.comments')
                  ),
                }),
                status: Yup.object().required(
                  t('owner.validators.reports.review.required.status')
                ),
              })}
              onSubmit={(values) => {
                handleSubmit(values);
              }}
            >
              <Tabs
                id="report-review-comments"
                defaultTab="new-comment"
                tabs={getTabItems()}
                tabPaneMinHeight="300px"
                contentOverflow="auto"
              />
              <div className="card mt-3 pt-3">
                <Row>
                  <Col>
                    <div className="d-flex flex-row justify-content-end align-items-center text-left">
                      <span className="pr-3 mb-3">
                        {t('owner.validators.reports.review.status')}
                      </span>
                      <DropdownField
                        id="status"
                        name="status"
                        label=""
                        options={STATUS_OPTIONS}
                        placeholder="--select--"
                        required
                        menuPortalTarget="parent"
                      />
                    </div>
                  </Col>
                  <Col>
                    <GreenButton className="mr-2 mt-1" type="submit">
                      {t('owner.validators.reports.review.submitBtn')}
                    </GreenButton>
                    <WhiteButton
                      className="mr-2 mt-1"
                      type="button"
                      onClick={handleCancelReview}
                    >
                      {t('owner.validators.reports.review.cancelBtn')}
                    </WhiteButton>
                  </Col>
                </Row>
              </div>
            </Form>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default CorpReportReview;
